import React, { useCallback } from 'react';
import { BulkAddClientRecord } from '../types';
import Stack, { contentStackSpacing } from '../../../../../v2BaseComponents/Stack';
import Headers from './Headers';
import { FlatList, ScrollView, View } from 'react-native';
import EditRow, { OnHoverChangeArgs, ROW_HEIGHT } from './EditRow';
import { ActionButtonLayout } from '../../../../../v2BaseComponents/ActionButtonLayout';
import { mainContentPadding } from '../../../../../v2BaseComponents/MainContentContainer';
import { Paper } from '../../../../../v2BaseComponents/Paper';
import { GridRefs, SetInputRefArgs } from '../../../../../v2BaseComponents/RichTable/types';
import { TableTopActions } from './TableTopActions';
import { RowActions } from './RowActions';
import { DOB_FORMAT, emptyBulkAddClientRecord, validateRecord } from './utils';
import { getIntl } from '../../../../../../utils/intl';
import { useHasErrors } from './useTableErrors';
import { useProfilesTabNavigation } from '../../../../../../utils/appNavigationNames';
import { useOrganizationClientRecords } from '../../../../../../hooks/api/useOrganizationClientRecords';
import { APITypes } from '../../../../../../primary-record-types';
import { DateTime } from 'luxon';
import { PRFlatList, PRScrollView } from '../../../../../shared/Scroll';

const intl = getIntl('profiles.bulkAddClientRecords.reviewAndEditContent', {
    header: 'Upload {count} Client Record{plural}',
    reUpload: 'Re-Upload File',
});

export type ReviewAndEditContentOwnProps = {
    initialRecords: BulkAddClientRecord[];
    onReupload: () => void;
};

export type ReviewAndEditContentProps = ReviewAndEditContentOwnProps;

export type RowModificationArgs =
    | {
          type: 'add';
          afterRowIndex: number;
      }
    | {
          type: 'delete';
          rowIndex: number;
      };

const getBulkAddRequest = (
    records: BulkAddClientRecord[],
): APITypes.organizationService.BulkCreateOrganizationClientRecordsRequest => {
    return {
        organizationClientRecords: records.map((record) => {
            const base: APITypes.organizationService.CreateOrganizationClientRecordRequest = {
                clientDetails: {},
                clientOrganizerDetails: {},
            };

            if (record.clientFirstName) {
                base.clientDetails.firstName = record.clientFirstName;
            }

            if (record.clientLastName) {
                base.clientDetails.lastName = record.clientLastName;
            }

            if (record.clientDOB && !validateRecord(record).clientDOB) {
                base.clientDetails.dob = DateTime.fromFormat(record.clientDOB, DOB_FORMAT).toISODate() || undefined;
            }

            if (record.clientPreferredName) {
                base.clientDetails.preferredName = record.clientPreferredName;
            }

            if (record.pointOfContactFirstName) {
                base.clientOrganizerDetails.firstName = record.pointOfContactFirstName;
            }

            if (record.pointOfContactLastName) {
                base.clientOrganizerDetails.lastName = record.pointOfContactLastName;
            }

            if (record.pointOfContactEmail) {
                base.clientOrganizerDetails.email = record.pointOfContactEmail;
            }

            if (record.pointOfContactPreferredName) {
                base.clientOrganizerDetails.preferredName = record.pointOfContactPreferredName;
            }

            if (record.pointOfContactPhoneNumber) {
                base.clientOrganizerDetails.phoneNumber = record.pointOfContactPhoneNumber;
            }

            return base;
        }),
    };
};

const ReviewAndEditContent: React.FC<ReviewAndEditContentProps> = (props) => {
    const profilesNavigator = useProfilesTabNavigation();
    const { isBulkAddingClientRecords, bulkAddClientRecords } = useOrganizationClientRecords({
        onBulkAddClientRecordsSuccess: () => profilesNavigator.navigate('root', {}),
    });
    const [hoveredRowIndex, setHoveredRowIndex] = React.useState<number | null>(null);
    const [initialScrollIndex, setInitialScrollIndex] = React.useState<number | null>(null);
    const scrollViewRef = React.useRef<ScrollView | null>(null);
    const flatListRef = React.useRef<FlatList | null>(null);

    const paperRef = React.useRef<View | null>(null);
    const gridInputRefs = React.useRef<GridRefs>([]);
    const onSetInputRef = useCallback((args: SetInputRefArgs) => {
        gridInputRefs.current[args.rowIndex] = gridInputRefs.current[args.rowIndex] || [];
        gridInputRefs.current[args.rowIndex][args.columnIndex] = args.ref;
    }, []);
    const [records, setRecords] = React.useState(props.initialRecords);
    const changeHandler = useCallback((value: BulkAddClientRecord, index: number) => {
        setRecords((records) => records.map((r, i) => (i === index ? value : r)));
    }, []);
    const hasErrors = useHasErrors(records);
    const hoverChangeHandler = useCallback(({ isHovered, rowIndex }: OnHoverChangeArgs) => {
        setHoveredRowIndex((existingHoverIndex) => {
            if (existingHoverIndex === rowIndex && !isHovered) {
                return null;
            } else if (!isHovered) {
                return existingHoverIndex;
            } else {
                return rowIndex;
            }
        });
    }, []);

    const [tableRemountKey, setTableRemountKey] = React.useState(0);
    const handleRowModification = (args: RowModificationArgs) => {
        setHoveredRowIndex(null);
        flatListRef.current = null;
        gridInputRefs.current = [];

        if (args.type === 'add') {
            setRecords((records) => {
                const newRecords = [...records];
                newRecords.splice(args.afterRowIndex + 1, 0, { ...emptyBulkAddClientRecord });
                return newRecords;
            });
        } else if (args.type === 'delete') {
            setRecords((records) => {
                const newRecords = [...records];
                newRecords.splice(args.rowIndex, 1);
                return newRecords;
            });
        }

        setTableRemountKey((key) => key + 1);

        const newRecordCount = records.length + (args.type === 'add' ? 1 : -1);
        if (newRecordCount === 0) return;

        const postUpdateScrollIndex = args.type === 'add' ? args.afterRowIndex + 1 : args.rowIndex;
        const validatedScrollIndex = Math.max(Math.min(newRecordCount - 1, postUpdateScrollIndex), 0);
        setInitialScrollIndex(validatedScrollIndex);
    };

    return (
        <ActionButtonLayout
            restrictedWidthVariant="lg"
            actions={[
                {
                    title: intl('reUpload'),
                    onPress: props.onReupload,
                    icon: 'XCircle',
                    displayInMoreMenu: false,
                    variant: 'secondary',
                    isDisabled: isBulkAddingClientRecords,
                    testID: 're-upload-file',
                },
                {
                    title: intl('header', { count: records.length, plural: records.length > 1 ? 's' : '' }),
                    onPress: () => {
                        const bulkAddRequest = getBulkAddRequest(records);
                        bulkAddClientRecords(bulkAddRequest);
                    },
                    icon: 'Upload',
                    isDisabled: hasErrors || isBulkAddingClientRecords,
                    displayInMoreMenu: false,
                    testID: 'upload-clients',
                },
            ]}
        >
            <Stack vertical fullWidth fullHeight padding={mainContentPadding} space={contentStackSpacing}>
                <TableTopActions
                    records={records}
                    horizontalScrollViewRef={scrollViewRef.current}
                    verticalFlatListRef={flatListRef.current}
                    gridInputRefs={gridInputRefs.current}
                />
                <Paper style={{ flex: 1, padding: 0 }} ref={paperRef}>
                    <Stack horizontal fullWidth fullHeight>
                        <PRScrollView ref={scrollViewRef} horizontal style={{ width: '100%', height: '100%', flex: 1 }}>
                            <Stack vertical>
                                <Headers firstRowValues={records[0]} />
                                <View style={{ flex: 1 }}>
                                    <PRFlatList
                                        key={tableRemountKey}
                                        ref={flatListRef}
                                        showsVerticalScrollIndicator={false}
                                        style={{ width: '100%', height: '100%' }}
                                        contentContainerStyle={{ width: '100%', height: '100%' }}
                                        data={records}
                                        windowSize={30}
                                        initialNumToRender={50}
                                        maxToRenderPerBatch={50}
                                        initialScrollIndex={initialScrollIndex}
                                        renderItem={({ item, index }) => (
                                            <EditRow
                                                value={item}
                                                onChange={changeHandler}
                                                nextValue={records[index + 1] || undefined}
                                                index={index}
                                                onSetInputRef={onSetInputRef}
                                                onHoverChange={hoverChangeHandler}
                                                container={paperRef}
                                                isDisabled={isBulkAddingClientRecords}
                                            />
                                        )}
                                        getItemLayout={(_, index) => ({
                                            length: ROW_HEIGHT,
                                            offset: ROW_HEIGHT * index,
                                            index,
                                        })}
                                    />
                                </View>
                            </Stack>
                        </PRScrollView>
                        <RowActions
                            gridInputRefs={gridInputRefs.current}
                            hoveredRowIndex={hoveredRowIndex}
                            containerRef={paperRef.current}
                            onAddRow={(afterIndex) => handleRowModification({ type: 'add', afterRowIndex: afterIndex })}
                            onDeleteRow={(index) => handleRowModification({ type: 'delete', rowIndex: index })}
                        />
                    </Stack>
                </Paper>
            </Stack>
        </ActionButtonLayout>
    );
};

export default React.memo(ReviewAndEditContent);
